/* eslint-disable no-plusplus */
/* eslint-disable @typescript-eslint/prefer-for-of */
import type { IAutoColumnWidthOptions, IBaseColumn, TTablePipeline, IPipelineProps } from '../_type';

// 快路径：判断是否纯 ASCII
const ASCII_RE = /^[\x00-\x7F]+$/;

// 计算字符串的显示长度（区分中英文字符和特殊符号），带快路径优化
export const getStringDisplayLength = (text: string): number => {
  if (!text) return 0;
  // 纯 ASCII 快路径：直接返回长度
  if (ASCII_RE.test(text)) return text.length;

  let length = 0;
  // 使用 codePoint 遍历，兼容代理对
  for (let i = 0;i < text.length;i++) {
    const code = text.codePointAt(i)!;
    if (code > 0xffff) i++;

    if ((code >= 0x4E00 && code <= 0x9FFF) || (code >= 0xFF00 && code <= 0xFFEF)) {
      length += 2;
    } else if (
      (code >= 0x30 && code <= 0x39) ||
      (code >= 0x41 && code <= 0x5A) ||
      (code >= 0x61 && code <= 0x7A)
    ) {
      length += 1;
    } else {
      length += 0.8;
    }
  }
  return length;
};

export const getTextWidth = (ctx: CanvasRenderingContext2D | null, text: string): number => {
  if (!ctx) return 0;
  return ctx.measureText(text).width;
};

export const autoColumnWidth = (pipeline: TTablePipeline, options: IAutoColumnWidthOptions = {}) => {
  try {
    const {
      maxWidth = 600,
      minWidth = 80,
      padding = 16,
      headerPadding = 20,
      sortWidth = 20,
      fontFamily = 'Roboto,"system-ui",Microsoft YaHei,Helvetica,Arial,Hiragino Sans GB,Heiti SC,Helvetica Neue,WenQuanYi Micro Hei,sans-serif',
      fontSize = 14,
      bufferWidth = 10,
      fontWeight = 'normal',
    } = options;
  
    // 创建临时 canvas 来计算文本宽度
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    }

    // 获取当前数据源和列配置
    const currentProps = pipeline.getProps() as unknown as IPipelineProps;
    const { dataSource, columns } = currentProps;

    // 需要自适应宽度的列（排除固定宽度列）
    const autoColCodes = new Set<string>();
    for (let c = 0;c < columns.length;c++) {
      const col = columns[c] as IBaseColumn;
      if (typeof col.width !== 'number') {
        autoColCodes.add(col.code);
      }
    }

    // 文本长度缓存（如果后面某列需要比较多个候选时避免重复测宽），比如“日活跃”之前出现过，那么下次不计算直接拿对应的文本长度就行
    const lengthCache = new Map<string, number>();
    const getLengthCached = (text: string): number => {
      const hit = lengthCache.get(text);
      if (hit !== undefined) return hit;
      const len = getStringDisplayLength(text);
      lengthCache.set(text, len);
      return len;
    };

    // 宽度缓存（如果后面某列需要比较多个候选时避免重复测宽），同上面，比如“日活跃”之前出现过，那么下次不计算直接拿对应的文本宽度就行
    const widthCache = new Map<string, number>();
    const getWidthCached = (text: string): number => {
      const hit = widthCache.get(text);
      if (hit !== undefined) return hit;
      const w = getTextWidth(ctx, text);
      widthCache.set(text, w);
      return w;
    };

    // 预计算：每列按“显示长度规则”的最长文本（按行遍历该行实际存在的键，避免行×列全交叉）
    const columnMaxText: Record<string, string> = {};
    for (let r = 0;r < dataSource.length;r++) {
      const row = dataSource[r] as Record<string, unknown>;
      const keys = Object.keys(row);
      for (let i = 0;i < keys.length;i++) {
        const code = keys[i];
        if (!autoColCodes.has(code)) continue;

        const raw = row[code];
        if (raw === undefined || raw === null) continue;
        const text = typeof raw === 'string' ? raw : String(raw);
        const displayLen = getLengthCached(text);

        const current = columnMaxText[code];
        if (!current) {
          columnMaxText[code] = text;
        } else {
          const currentLen = getLengthCached(current);
          if (displayLen > currentLen) {
            columnMaxText[code] = text;
          }
        }
      }
    }

    // 列宽计算：仅比较表头宽度与预计算最大文本的测宽
    const calculateColumnWidth = (column: IBaseColumn): number => {
      let maxContentWidth = 0;

      // 表头宽度
      if (column.name) {
        const headerWidth = getTextWidth(ctx, String(column?.name)) + headerPadding * 2 + sortWidth;
        maxContentWidth = Math.max(maxContentWidth, headerWidth);
      }

      // 内容宽度（预计算候选 + 单次测宽）
      const maxText = columnMaxText[column.code] || '';
      if (maxText) {
        const width = getWidthCached(maxText);
        const cellWidth = width + padding * 2;
        maxContentWidth = Math.max(maxContentWidth, cellWidth);
      }

      return Math.min(Math.max(maxContentWidth, minWidth), maxWidth);
    };
  
    // 生成最终列配置
    const adjustedColumns = columns.map((column: IBaseColumn) => {
      if (typeof column.width === 'number') {
        return column;
      }
  
      const calculatedWidth = calculateColumnWidth(column);
      return {
        ...column,
        width: calculatedWidth + bufferWidth,
        headerCellStyle: {
          ...column.headerCellStyle,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        cellStyle: {
          ...column.cellStyle,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }
      };
    });

    pipeline.columns(adjustedColumns as any);
    return pipeline;
  } catch (error) {
    console.log('autoColumnWidth:~error', error);
    return pipeline;
  }
};